'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

import { BRAND } from '@kit/shared/brand';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isDev = process.env.NODE_ENV !== 'production';

  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        errorType: 'global_error_boundary',
        errorDigest: error.digest || 'no_digest',
        isServerComponentError: error.message?.includes('Server Components')
          ? 'true'
          : 'false',
      },
      extra: {
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
        errorDigest: error.digest,
        errorCause: error.cause,
      },
    });
  }, [error]);

  return (
    <html lang="en">
      <body>
        <div
          style={{
            display: 'flex',
            minHeight: '100vh',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '28rem',
              textAlign: 'center',
            }}
          >
            <h1
              style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                margin: '0 0 1rem',
              }}
            >
              {/* i18n-ignore: global-error is the Next.js root error boundary;
                  it replaces the root layout, so no I18nProvider/Trans context
                  exists here. Literals are intentional last-resort English. */}
              {'Something went wrong'}
            </h1>
            <p style={{ color: '#6b7280', margin: '0 0 1.5rem' }}>
              {/* i18n-ignore: root error boundary has no i18n provider */}
              {"We've been notified and are working to fix the issue."}
            </p>

            <details
              style={{
                textAlign: 'left',
                background: '#f3f4f6',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1.5rem',
                fontSize: '0.75rem',
              }}
            >
              <summary style={{ cursor: 'pointer', color: '#6b7280' }}>
                {/* i18n-ignore: root error boundary has no i18n provider */}
                {'Show details'}
              </summary>
              <div style={{ marginTop: '0.5rem' }}>
                {error.digest && (
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Reference:</strong>{' '}
                    <code
                      style={{
                        background: '#e5e7eb',
                        padding: '0.125rem 0.25rem',
                        borderRadius: '0.25rem',
                      }}
                    >
                      {error.digest}
                    </code>
                  </p>
                )}
                {isDev && error.message && (
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Message:</strong> {error.message}
                  </p>
                )}
                {isDev && error.stack && (
                  <pre
                    style={{
                      marginTop: '0.5rem',
                      maxHeight: '12rem',
                      overflow: 'auto',
                      background: '#e5e7eb',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.625rem',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {error.stack}
                  </pre>
                )}
              </div>
            </details>

            {/* Directed recovery actions. The root boundary replaces the app
                shell, so a user who lands here has no nav — without these it's
                a dead end. Literals are intentional English (no i18n provider). */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                justifyContent: 'center',
              }}
            >
              {/* eslint-disable-next-line kit-components/use-kit-primitives -- root error boundary renders outside app providers/theme; @kit Button would pull in context/styling not guaranteed at this level */}
              <button
                onClick={reset}
                style={{
                  display: 'inline-flex',
                  height: '2.5rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#fff',
                  backgroundColor: '#18181b',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {/* i18n-ignore: root error boundary has no i18n provider */}
                {'Try again'}
              </button>

              <a
                href="/auth/sign-in"
                style={{
                  display: 'inline-flex',
                  height: '2.5rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#18181b',
                  backgroundColor: '#fff',
                  border: '1px solid #d4d4d8',
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
              >
                {/* i18n-ignore: root error boundary has no i18n provider */}
                {'Go to sign in'}
              </a>

              <a
                href="/"
                style={{
                  display: 'inline-flex',
                  height: '2.5rem',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#18181b',
                  backgroundColor: '#fff',
                  border: '1px solid #d4d4d8',
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
              >
                {/* i18n-ignore: root error boundary has no i18n provider */}
                {'Go home'}
              </a>
            </div>

            <p
              style={{
                color: '#6b7280',
                margin: '1rem 0 0',
                fontSize: '0.8125rem',
              }}
            >
              {/* i18n-ignore: root error boundary has no i18n provider */}
              {'Still stuck? Email '}
              <a
                href={`mailto:${BRAND.emails.support}`}
                style={{ color: '#18181b' }}
              >
                {BRAND.emails.support}
              </a>
            </p>
          </div>
        </div>
      </body>
    </html>
  );
}
